import React, { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Input,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  ClearOutlined,
  LinkOutlined,
  ReloadOutlined,
  SearchOutlined,
  TrophyOutlined,
} from '@ant-design/icons';
import styled from 'styled-components';
import { useAppDispatch, useAppSelector } from '../../Modules/Presentation/hooks';
import { selectUser } from '../../Modules/User/Presentation/selectors';
import {
  cancelUrlRankLookup,
  lookupUrlRank,
} from '../../Modules/UrlRank/Presentation/actions';
import { clearUrlRankState } from '../../Modules/UrlRank/Presentation/reducers';
import {
  selectUrlRankError,
  selectUrlRankLoading,
  selectUrlRankResult,
} from '../../Modules/UrlRank/Presentation/selectors';
import {
  UrlRankRequest,
  UrlRankResult,
  isUrlRankPendingStatus,
  isUrlRankTerminalStatus,
} from '../../Modules/UrlRank/Model/UrlRank';

const { Title, Text } = Typography;

const PageContainer = styled.div`
  max-width: 960px;
  margin: 0 auto;
`;

const HeaderCard = styled(Card)`
  text-align: center;
  margin-bottom: 16px;
  border-radius: 8px;
`;

const InputCard = styled(Card)`
  margin-bottom: 16px;
  border-radius: 8px;
`;

const ResultCard = styled(Card)`
  border-radius: 8px;
`;

const FormField = styled.div`
  margin-bottom: 16px;

  .field-label {
    display: block;
    margin-bottom: 6px;
    font-size: 14px;
    font-weight: 600;
    color: #333;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 12px;
`;

const InfoRow = styled.div`
  padding: 10px 12px;
  background: #f8f9fa;
  border-radius: 8px;
  word-break: break-all;
`;

const RankHero = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 28px 16px;
  border-radius: 8px;
  background: #fafafa;
  border: 1px solid #f0f0f0;
`;

function getRankDisplay(result: UrlRankResult) {
  const status = result.rank_check_status;

  if (isUrlRankPendingStatus(status)) {
    return {
      title: '확인 중',
      tag: <Tag color="processing">확인 중</Tag>,
      hint: result.message || '잠시만 기다려주세요.',
    };
  }

  if (status === 'failed') {
    return {
      title: '실패',
      tag: <Tag color="error">안 됨</Tag>,
      hint: '조회에 실패했습니다. 다시 시도해주세요.',
    };
  }

  if (status === 'partial') {
    return {
      title: '확인 불가',
      tag: <Tag color="warning">확인 불가</Tag>,
      hint: '순위를 확실히 확인하지 못했습니다. 다시 시도해주세요.',
    };
  }

  if (result.rank == null) {
    return {
      title: '순위 밖',
      tag: <Tag color="default">순위 밖</Tag>,
      hint: '해당 키워드에서는 순위를 찾지 못했습니다.',
    };
  }

  return {
    title: `${result.rank}위`,
    tag: (
      <Tag
        color={result.rank <= 3 ? 'gold' : 'blue'}
        style={{ fontSize: 16, padding: '4px 14px' }}
      >
        {result.rank}위
      </Tag>
    ),
    hint: '조회가 완료되었습니다.',
  };
}

const UrlRankPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(selectUser);
  const loading = useAppSelector(selectUrlRankLoading);
  const result = useAppSelector(selectUrlRankResult);
  const error = useAppSelector(selectUrlRankError);

  const [formData, setFormData] = useState<UrlRankRequest>({
    keyword: '',
    product_url: '',
    force_refresh: false,
  });

  const pending = isUrlRankPendingStatus(result?.rank_check_status);
  const busy = loading || pending;

  const handleInputChange = useCallback(
    (field: 'keyword' | 'product_url', value: string) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
    },
    [],
  );

  const handleSubmit = useCallback(async () => {
    if (!formData.keyword.trim() || !formData.product_url.trim()) {
      message.error('키워드와 상품 링크를 모두 입력해주세요.');
      return;
    }

    try {
      dispatch(clearUrlRankState());
      const data = await dispatch(lookupUrlRank(formData)).unwrap();

      if (isUrlRankPendingStatus(data.rank_check_status)) {
        message.info(data.message || '아직 확인 중입니다. 잠시 후 다시 조회해주세요.');
        return;
      }

      if (!isUrlRankTerminalStatus(data.rank_check_status)) {
        return;
      }

      if (data.rank_check_status === 'failed') {
        message.error('조회에 실패했습니다.');
      } else if (data.rank_check_status === 'partial') {
        message.warning('순위를 확인하지 못했습니다. 다시 시도해주세요.');
      } else if (data.rank == null) {
        message.info('순위 밖입니다.');
      } else {
        message.success(`${data.rank}위로 확인되었습니다.`);
      }
    } catch {
      // error state already set by thunk
    }
  }, [dispatch, formData]);

  const handleClear = useCallback(() => {
    cancelUrlRankLookup();
    dispatch(clearUrlRankState());
    setFormData({ keyword: '', product_url: '', force_refresh: false });
  }, [dispatch]);

  const rankDisplay = useMemo(
    () => (result ? getRankDisplay(result) : null),
    [result],
  );

  if (!currentUser || currentUser.grade !== 'ADMIN') {
    return (
      <PageContainer>
        <Alert
          message="접근 권한이 없습니다"
          description="관리자만 사용할 수 있습니다."
          type="warning"
          showIcon
          style={{ marginTop: 16 }}
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <HeaderCard>
        <Title level={2} style={{ margin: 0 }}>
          URL 순위 조회
        </Title>
        <Text type="secondary" style={{ fontSize: 14 }}>
          키워드와 상품 링크로 현재 순위를 확인합니다
        </Text>
      </HeaderCard>

      <InputCard title="조회하기">
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <FormField>
            <label className="field-label">키워드 *</label>
            <Input
              size="large"
              placeholder="예: 무선청소기"
              value={formData.keyword}
              onChange={(e) => handleInputChange('keyword', e.target.value)}
              onPressEnter={handleSubmit}
              disabled={busy}
            />
          </FormField>

          <FormField>
            <label className="field-label">상품 링크 *</label>
            <Input
              size="large"
              prefix={<LinkOutlined />}
              placeholder="https://smartstore.naver.com/..."
              value={formData.product_url}
              onChange={(e) => handleInputChange('product_url', e.target.value)}
              onPressEnter={handleSubmit}
              disabled={busy}
            />
          </FormField>

          <Checkbox
            checked={Boolean(formData.force_refresh)}
            disabled={busy}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                force_refresh: e.target.checked,
              }))
            }
          >
            다시 새로 조회하기
          </Checkbox>

          <ButtonContainer>
            <Button
              type="primary"
              size="large"
              icon={formData.force_refresh ? <ReloadOutlined /> : <SearchOutlined />}
              onClick={handleSubmit}
              loading={busy}
              disabled={!formData.keyword.trim() || !formData.product_url.trim()}
            >
              {busy ? '확인 중...' : '조회하기'}
            </Button>
            <Button
              size="large"
              icon={<ClearOutlined />}
              onClick={handleClear}
            >
              초기화
            </Button>
          </ButtonContainer>
        </Space>
      </InputCard>

      {error && (
        <ResultCard style={{ marginBottom: 16 }}>
          <Alert
            message="조회에 실패했습니다"
            description="잠시 후 다시 시도해주세요."
            type="error"
            showIcon
          />
        </ResultCard>
      )}

      {result && rankDisplay && (
        <ResultCard
          title={
            <Space>
              <TrophyOutlined style={{ color: '#faad14' }} />
              결과
            </Space>
          }
        >
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <RankHero>
              {pending ? <Spin /> : null}
              <Text type="secondary">순위</Text>
              <Title level={2} style={{ margin: 0 }}>
                {rankDisplay.title}
              </Title>
              {rankDisplay.tag}
              <Text type="secondary" style={{ fontSize: 12 }}>
                {rankDisplay.hint}
              </Text>
            </RankHero>

            <InfoRow>
              <Text strong>키워드: </Text>
              {result.keyword || formData.keyword || '-'}
            </InfoRow>
            <InfoRow>
              <Text strong>상품 링크: </Text>
              {result.product_url || formData.product_url ? (
                <a
                  href={result.product_url || formData.product_url}
                  target="_blank"
                  rel="noreferrer"
                >
                  {result.product_url || formData.product_url}
                </a>
              ) : (
                '-'
              )}
            </InfoRow>
          </Space>
        </ResultCard>
      )}
    </PageContainer>
  );
};

export default UrlRankPage;
